import dayjs from 'dayjs';
import pluginDuration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import pluginIsSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import dayjsGenerateConfig from 'bonree-picker/lib/generate/dayjs';
import { createTimeZoneConfig, isValidTimeZone, parseTzOffset, } from 'bonree-picker/lib/utils/timeZoneUtil';
import 'dayjs/locale/en-gb';
dayjs.extend(pluginDuration);
dayjs.extend(isSameOrBefore);
dayjs.extend(pluginIsSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);
export const MAX_DURATION = 86400000000;
const ABSOLUTE_TIME_FORMATS = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD',
    'YYYY-MM',
    'YYYY HH:mm',
    'YYYY',
    'HH:mm',
];
const getValidTimeZone = (timeZone) => timeZone && isValidTimeZone(timeZone) ? timeZone : undefined;
const tzOffsetMinutesCache = new Map();
const COMMON_CONFIG_SESSION_STORAGE_KEY = 'KEY_ONE_COMMON_CONFIG';
export const getCommonTimeZoneConfig = () => {
    if (typeof sessionStorage === 'undefined')
        return {};
    try {
        const config = JSON.parse(sessionStorage.getItem(COMMON_CONFIG_SESSION_STORAGE_KEY) || '{}');
        return {
            timeZone: config === null || config === void 0 ? void 0 : config.timezone,
            tzOffset: config === null || config === void 0 ? void 0 : config.tzOffset,
        };
    }
    catch (_a) {
        return {};
    }
};
const getTzOffsetMinutes = (tzOffset) => {
    if (!tzOffset)
        return null;
    if (!tzOffsetMinutesCache.has(tzOffset)) {
        tzOffsetMinutesCache.set(tzOffset, parseTzOffset(tzOffset));
    }
    return tzOffsetMinutesCache.get(tzOffset);
};
const projectDate = (date, timeZone, tzOffset) => {
    const offsetMinutes = getTzOffsetMinutes(tzOffset);
    if (offsetMinutes !== null) {
        return date.utcOffset() === offsetMinutes ? date : date.utcOffset(offsetMinutes);
    }
    const validTimeZone = getValidTimeZone(timeZone);
    return validTimeZone ? date.tz(validTimeZone) : date;
};
const parseFixedOffsetDate = (value, format, offsetMinutes) => dayjs.utc(value, format).subtract(offsetMinutes, 'minute').utcOffset(offsetMinutes);
/** 获取指定时区的当前时间；无效或未传时区时保持原有的本地时区行为。 */
export const getTimeZoneNow = (timeZone, tzOffset) => projectDate(dayjs(), timeZone, tzOffset);
/** 仅按指定时区格式化，不改变传入值代表的时间点。 */
export const formatInTimeZone = (date, format, timeZone, tzOffset) => projectDate(date, timeZone, tzOffset).format(format);
const subtractInTimeZone = (date, cycle, unit, timeZone, tzOffset) => {
    const offsetMinutes = getTzOffsetMinutes(tzOffset);
    const validTimeZone = getValidTimeZone(timeZone);
    if (offsetMinutes === null && !validTimeZone)
        return date.subtract(cycle, unit);
    // 日/月/年属于日历运算，需要处理 DST；适配器与 formatter 均按时区缓存。
    const generateConfig = createTimeZoneConfig(dayjsGenerateConfig, validTimeZone, offsetMinutes === null ? undefined : tzOffset);
    let result;
    if (unit === 'y') {
        result = generateConfig.addYear(date, -cycle);
    }
    else if (unit === 'M') {
        result = generateConfig.addMonth(date, -cycle);
    }
    else if (unit === 'd') {
        result = generateConfig.addDate(date, -cycle);
    }
    else if (unit === 'w') {
        result = generateConfig.addDate(date, -cycle * 7);
    }
    else {
        result = date.subtract(cycle, unit);
    }
    // 时长运算可能跨越 DST，重新投影以刷新展示偏移量。
    return projectDate(dayjs(result.valueOf()), validTimeZone, tzOffset);
};
const startOfInTimeZone = (date, unit, timeZone, tzOffset) => {
    const offsetMinutes = getTzOffsetMinutes(tzOffset);
    if (offsetMinutes === null && unit !== 'w')
        return date.startOf(unit);
    const generateConfig = createTimeZoneConfig(dayjsGenerateConfig, timeZone, tzOffset);
    let result = date;
    if (unit === 'y') {
        result = generateConfig.setMonth(result, 0);
        result = generateConfig.setDate(result, 1);
    }
    else if (unit === 'M') {
        result = generateConfig.setDate(result, 1);
    }
    else if (unit === 'w') {
        const weekDay = generateConfig.getWeekDay(result);
        result = generateConfig.addDate(result, -((weekDay + 6) % 7));
    }
    if (unit === 'y' || unit === 'M' || unit === 'w' || unit === 'd') {
        result = generateConfig.setHour(result, 0);
    }
    if (unit !== 's' && unit !== 'm') {
        result = generateConfig.setMinute(result, 0);
    }
    if (unit !== 's') {
        result = generateConfig.setSecond(result, 0);
    }
    return projectDate(result, timeZone, tzOffset).millisecond(0);
};
const endOfDayInTimeZone = (date, timeZone, tzOffset) => {
    if (getTzOffsetMinutes(tzOffset) === null)
        return date.endOf('day');
    const start = startOfInTimeZone(date, 'd', timeZone, tzOffset);
    const generateConfig = createTimeZoneConfig(dayjsGenerateConfig, timeZone, tzOffset);
    return projectDate(generateConfig.addDate(start, 1), timeZone, tzOffset).subtract(1, 'millisecond');
};
export const getTimeZoneStartOfDay = (timeZone, tzOffset) => startOfInTimeZone(getTimeZoneNow(timeZone, tzOffset), 'd', timeZone, tzOffset);
export const getTimeZoneEndOfDay = (timeZone, tzOffset) => endOfDayInTimeZone(getTimeZoneNow(timeZone, tzOffset), timeZone, tzOffset);
export function getPlaceholder(picker, locale, customizePlaceholder) {
    if (customizePlaceholder !== undefined) {
        return customizePlaceholder;
    }
    if (picker === 'year' && locale.lang.yearPlaceholder) {
        return locale.lang.yearPlaceholder;
    }
    if (picker === 'quarter' && locale.lang.quarterPlaceholder) {
        return locale.lang.quarterPlaceholder;
    }
    if (picker === 'month' && locale.lang.monthPlaceholder) {
        return locale.lang.monthPlaceholder;
    }
    if (picker === 'week' && locale.lang.weekPlaceholder) {
        return locale.lang.weekPlaceholder;
    }
    if (picker === 'time' && locale.timePickerLocale.placeholder) {
        return locale.timePickerLocale.placeholder;
    }
    return locale.lang.placeholder;
}
export function getRangePlaceholder(picker, locale, customizePlaceholder) {
    if (customizePlaceholder !== undefined) {
        return customizePlaceholder;
    }
    if (picker === 'year' && locale.lang.yearPlaceholder) {
        return locale.lang.rangeYearPlaceholder;
    }
    if (picker === 'month' && locale.lang.monthPlaceholder) {
        return locale.lang.rangeMonthPlaceholder;
    }
    if (picker === 'week' && locale.lang.weekPlaceholder) {
        return locale.lang.rangeWeekPlaceholder;
    }
    if (picker === 'time' && locale.timePickerLocale.placeholder) {
        return locale.timePickerLocale.rangePlaceholder;
    }
    return locale.lang.rangePlaceholder;
}
export var GlobalPickerType;
(function (GlobalPickerType) {
    GlobalPickerType[GlobalPickerType["shortcut"] = 1] = "shortcut";
    GlobalPickerType[GlobalPickerType["custom"] = 2] = "custom";
    GlobalPickerType[GlobalPickerType["recently"] = 3] = "recently";
    GlobalPickerType[GlobalPickerType["special"] = 4] = "special";
})(GlobalPickerType || (GlobalPickerType = {}));
export function getTargetFromEvent(e) {
    var _a;
    const target = e.target;
    // get target if in shadow dom
    if (e.composed && target.shadowRoot) {
        return (((_a = e.composedPath) === null || _a === void 0 ? void 0 : _a.call(e)[0]) || target);
    }
    return target;
}
export function elementsContains(elements, target) {
    return elements.some(ele => {
        let isContain = false;
        if (ele) {
            // @ts-ignore
            if (ele.input) {
                // @ts-ignore
                isContain = ele.input.contains(target);
            }
            else {
                isContain = ele.contains(target);
            }
        }
        return isContain;
    });
}
let globalClickFunc = null;
const clickCallbacks = new Set();
export function addGlobalMouseDownEvent(callback) {
    if (!globalClickFunc && typeof window !== 'undefined' && window.addEventListener) {
        globalClickFunc = (e) => {
            // Clone a new list to avoid repeat trigger events
            [...clickCallbacks].forEach(queueFunc => {
                queueFunc(e);
            });
        };
        window.addEventListener('mousedown', globalClickFunc);
    }
    clickCallbacks.add(callback);
    return () => {
        clickCallbacks.delete(callback);
        if (clickCallbacks.size === 0) {
            window.removeEventListener('mousedown', globalClickFunc);
            globalClickFunc = null;
        }
    };
}
// =======================
// ===== 全局时间组件 =========
// ========================
// 生成刷新频率
export const generateRefreshOptions = (locale) => [
    { label: locale === null || locale === void 0 ? void 0 : locale.noRefresh, value: 0 },
    { label: `1 ${locale === null || locale === void 0 ? void 0 : locale.minutes}`, value: 60 },
    { label: `3 ${locale === null || locale === void 0 ? void 0 : locale.minutes}`, value: 3 * 60 },
    { label: `5 ${locale === null || locale === void 0 ? void 0 : locale.minutes}`, value: 5 * 60 },
    { label: `15 ${locale === null || locale === void 0 ? void 0 : locale.minutes}`, value: 15 * 60 },
];
export const isAbsoluteTime = (value) => /^\d[\d:-\s]+\d$/.test(value);
export const isRelativeTime = (value) => /-?\d?\/?([Mdhmswy]+?)/.test(value);
export const isSpecialTime = (value) => /%[A-Z_a-z]+%/.test(value);
export const multiSpaceToOne = (value) => value.replace(/\s+/g, ' ');
export function valueToShortcut(value) {
    return {
        type: value.split('~').some(item => isAbsoluteTime(item.trim()))
            ? GlobalPickerType.custom
            : GlobalPickerType.shortcut,
        label: value,
        value,
    };
}
// // 合并时间值与日期值
// export function mergeTimeAndDate(date: Dayjs, time: Dayjs) {
//   return dayjs(date).hour(time.hour()).minute(time.minute()).second(time.second());
// }
// duration单位是毫秒
function generateDuration(duration) {
    const diffTime = dayjs.duration(duration);
    const d = Math.floor(diffTime.asDays());
    const h = diffTime.hours();
    const m = diffTime.minutes();
    const s = diffTime.seconds();
    const ms = diffTime.asMilliseconds();
    return {
        d,
        h,
        m,
        s,
        ms,
    };
}
// 计算开始时间与结束时间之间的长度
export function computedDuration(dates) {
    let res;
    if (dates && dates[0] && dates[1]) {
        res = generateDuration(dates[1].valueOf() - dates[0].valueOf());
    }
    return res;
}
// 计算时间间隔文本
export function durationText(dates, maxDuration, locale) {
    let isGreater = false;
    let isEqual = false;
    let isInvalid = false;
    let formatString = locale === null || locale === void 0 ? void 0 : locale.invalidTimeRange;
    if (dates && dates[0] && dates[1]) {
        if (dates[1].valueOf() - dates[0].valueOf() > maxDuration) {
            isGreater = true;
            const durationObj = generateDuration(maxDuration);
            if (durationObj && locale) {
                isInvalid = false;
                formatString = `${locale === null || locale === void 0 ? void 0 : locale.noGreater} ${durationObj.d > 0 ? `${durationObj.d} ${locale === null || locale === void 0 ? void 0 : locale.day} ` : ''} ${durationObj.h > 0 ? `${durationObj.h} ${locale === null || locale === void 0 ? void 0 : locale.hour} ` : ''} ${durationObj.m > 0 ? `${durationObj.m} ${locale === null || locale === void 0 ? void 0 : locale.minute} ` : ''} ${durationObj.s > 0 ? `${durationObj.s} ${locale === null || locale === void 0 ? void 0 : locale.second}` : ''}`;
            }
        }
        else {
            const durationObj = computedDuration(dates);
            if (durationObj && locale) {
                isInvalid = false;
                formatString = `${durationObj.d > 0 ? `${durationObj.d} ${locale === null || locale === void 0 ? void 0 : locale.day} ` : ''}${durationObj.h > 0 ? `${durationObj.h} ${locale === null || locale === void 0 ? void 0 : locale.hour} ` : ''}${durationObj.m > 0 ? `${durationObj.m} ${locale === null || locale === void 0 ? void 0 : locale.minute} ` : ''}${durationObj.s > 0 ? `${durationObj.s} ${locale === null || locale === void 0 ? void 0 : locale.second}` : ''}`;
                if (!formatString.trim()) {
                    formatString = locale === null || locale === void 0 ? void 0 : locale.noEqual;
                    isEqual = true;
                }
            }
        }
    }
    else {
        isInvalid = true;
    }
    return { isInvalid, isGreater, isEqual, formatString };
}
// 是否是当前时间或当前时间之后
function isSameAfterNow(date, timeZone, tzOffset) {
    return (date &&
        date
            .second(0)
            .millisecond(0)
            .isSameOrAfter(getTimeZoneNow(timeZone, tzOffset).second(0).millisecond(0)));
}
// 将dayjs日期值转为value值, dayjs => -2h
function dateToValue(date, timeZone, tzOffset) {
    if (date) {
        if (isSameAfterNow(date, timeZone, tzOffset)) {
            return 'now';
        }
        let value = '';
        const duration = computedDuration([date, getTimeZoneNow(timeZone, tzOffset)]);
        if (duration) {
            ['y', 'M', 'd', 'h', 'm'].forEach(key => {
                if (duration[key] > 0) {
                    value += `${duration[key]}${key}`;
                }
            });
        }
        return `-${value}`;
    }
    return '';
}
// [dayjs, dayjs] => -2h ~ -1h
export function datesToValue(dates, timeZone, tzOffset) {
    return (dates === null || dates === void 0 ? void 0 : dates.map(item => dateToValue(item, timeZone, tzOffset)).join(' ~ ')) || '';
}
// 将日期对齐到整秒，避免 dayjs() 携带的毫秒导致时长计算少一秒
// 避免 timezone 插件在 DST 切换日对 startOf('second') 重算出错误偏移。
const normalizeToSecond = (date) => (date ? date.millisecond(0) : null);
// 解析相对单个时间
const parseRelativeTime = (value, now, timeZone, tzOffset) => {
    if (!value) {
        return null;
    }
    // 生成actionArr
    const [unitString, originString] = value.split('/');
    let actionArr = [];
    if (unitString && !/\.\d/.test(unitString)) {
        const myRe = /-?(\d+?)([Mdhmswy]+?)/g;
        let match = myRe.exec(unitString);
        // 是否全匹配
        let isAllMatch = true;
        while (match !== null) {
            actionArr.push({
                unit: match[2],
                cycle: Number(match[1]),
                isSubtract: true,
            });
            isAllMatch = match[0].length + match.index === unitString.length;
            match = myRe.exec(unitString);
        }
        if (!isAllMatch) {
            actionArr = [];
        }
    }
    if (originString) {
        actionArr.push({ unit: originString, isStartOf: true });
    }
    // 通过actionArr生成dayjs
    if (actionArr.length) {
        let resultDate = now;
        actionArr.forEach(item => {
            if (item.isSubtract) {
                resultDate = subtractInTimeZone(resultDate, Number(item.cycle), item.unit, timeZone, tzOffset);
            }
            else if (item.isStartOf) {
                resultDate = startOfInTimeZone(resultDate, item.unit, timeZone, tzOffset);
            } /*  */
        });
        return normalizeToSecond(resultDate);
    }
    return null;
};
// 解析单个时间缩写
const parseSingleAbbreviation = (value, now, timeZone, tzOffset) => {
    let date = null;
    if (isAbsoluteTime(value)) {
        // 绝对时间
        const offsetMinutes = getTzOffsetMinutes(tzOffset);
        const validTimeZone = getValidTimeZone(timeZone);
        if (offsetMinutes !== null || validTimeZone) {
            const matchedFormat = ABSOLUTE_TIME_FORMATS.find(format => dayjs(value, format, true).isValid());
            if (matchedFormat === 'HH:mm') {
                const dateText = `${now.format('YYYY-MM-DD')} ${value}`;
                date =
                    offsetMinutes !== null
                        ? parseFixedOffsetDate(dateText, 'YYYY-MM-DD HH:mm', offsetMinutes)
                        : dayjs.tz(dateText, 'YYYY-MM-DD HH:mm', validTimeZone);
            }
            else if (matchedFormat) {
                date =
                    offsetMinutes !== null
                        ? parseFixedOffsetDate(value, matchedFormat, offsetMinutes)
                        : dayjs.tz(value, matchedFormat, validTimeZone);
            }
        }
        else {
            date = dayjs(value, ABSOLUTE_TIME_FORMATS, true);
        }
        date = date && !Number.isNaN(date.valueOf()) ? normalizeToSecond(date) : null;
    }
    else if (isRelativeTime(value)) {
        // 相对时间
        date = parseRelativeTime(value, now, timeZone, tzOffset);
    }
    return date;
};
export const parseShortcutValue = (value, timeZone, tzOffset) => {
    //  不能含有汉字
    if (value && !/[\u4e00-\u9fa5]/.test(value)) {
        // value不包含 ~ now 时补齐
        let mergedValue = value;
        if (!(value === null || value === void 0 ? void 0 : value.includes('now')) && isRelativeTime(value)) {
            mergedValue = `${value} ~ now`;
        }
        const [start, end] = mergedValue.split('~').map(s => s.trim());
        // 同一次解析复用一个 now，避免跨秒边界及重复时区计算。
        const now = getTimeZoneNow(timeZone, tzOffset);
        const startDate = parseSingleAbbreviation(start, now, timeZone, tzOffset);
        const endDate = end === 'now'
            ? normalizeToSecond(now)
            : parseSingleAbbreviation(end, now, timeZone, tzOffset);
        return [startDate, endDate];
    }
    return null;
};
// =======================
// ===== 快捷方式 =========
// ========================
/**
 * Convert only absolute wall-time fragments in a shortcut value between time zones.
 * Relative and special-time expressions must stay relative to the time when they are used.
 */
export const convertShortcutValueTimeZone = (value, sourceTimeZone, sourceTzOffset, targetTimeZone, targetTzOffset) => {
    if (!value || (sourceTimeZone === targetTimeZone && sourceTzOffset === targetTzOffset)) {
        return value;
    }
    const sourceNow = getTimeZoneNow(sourceTimeZone, sourceTzOffset);
    return value
        .split('~')
        .map(fragment => {
        const wallTime = fragment.trim();
        if (!isAbsoluteTime(wallTime))
            return wallTime;
        const matchedFormat = ABSOLUTE_TIME_FORMATS.find(format => dayjs(wallTime, format, true).isValid());
        const date = parseSingleAbbreviation(wallTime, sourceNow, sourceTimeZone, sourceTzOffset);
        return matchedFormat && date
            ? formatInTimeZone(date, matchedFormat, targetTimeZone, targetTzOffset)
            : wallTime;
    })
        .join(' ~ ');
};
/**
 * Build a timezone-independent identity for an absolute range. Dynamic shortcut values keep
 * their normalized expression as identity because converting them to timestamps is unstable.
 */
export const getShortcutValueId = (value, timeZone, tzOffset) => {
    const normalizedValue = multiSpaceToOne(value.trim());
    const fragments = normalizedValue.split('~').map(fragment => fragment.trim());
    if (fragments.length === 2 && fragments.every(fragment => isAbsoluteTime(fragment))) {
        const dates = parseShortcutValue(normalizedValue, timeZone, tzOffset);
        if ((dates === null || dates === void 0 ? void 0 : dates[0]) && (dates === null || dates === void 0 ? void 0 : dates[1])) {
            return `absolute:${dates[0].valueOf()}:${dates[1].valueOf()}`;
        }
    }
    return `expression:${normalizedValue}`;
};
export const createRecentShortcutOption = (shortcut, value, timeZone, tzOffset) => (Object.assign(Object.assign({}, shortcut), { id: shortcut.id || getShortcutValueId(value, timeZone, tzOffset), label: shortcut.label === shortcut.value ? value : shortcut.label, value,
    timeZone, tzOffset: tzOffset || '' }));
export const normalizeCachedShortcutOptions = (shortcuts, targetTimeZone, targetTzOffset, fallbackTimeZoneConfig = {}) => {
    const migratedValues = new Map();
    const recentIds = new Set();
    const options = [];
    shortcuts.forEach(shortcut => {
        const hasOwnTimeZoneConfig = Boolean(shortcut.timeZone || shortcut.tzOffset);
        const sourceTimeZone = hasOwnTimeZoneConfig
            ? shortcut.timeZone
            : fallbackTimeZoneConfig.timeZone;
        const sourceTzOffset = hasOwnTimeZoneConfig
            ? shortcut.tzOffset
            : fallbackTimeZoneConfig.tzOffset;
        const normalizedSourceTzOffset = sourceTzOffset || undefined;
        const normalizedTargetTzOffset = targetTzOffset || undefined;
        const id = shortcut.id || getShortcutValueId(shortcut.value, sourceTimeZone, normalizedSourceTzOffset);
        const value = (sourceTimeZone || normalizedSourceTzOffset) &&
            (sourceTimeZone !== targetTimeZone || normalizedSourceTzOffset !== normalizedTargetTzOffset)
            ? convertShortcutValueTimeZone(shortcut.value, sourceTimeZone, normalizedSourceTzOffset, targetTimeZone, normalizedTargetTzOffset)
            : shortcut.value;
        if (value !== shortcut.value)
            migratedValues.set(shortcut.value, value);
        if (!recentIds.has(id)) {
            recentIds.add(id);
            options.push(createRecentShortcutOption(Object.assign(Object.assign({}, shortcut), { id }), value, targetTimeZone, targetTzOffset));
        }
    });
    return { options, migratedValues };
};
export const upsertShortcutOptionById = (shortcuts, shortcut, maxLength = 10) => [shortcut, ...shortcuts.filter(item => item.id !== shortcut.id)].slice(0, maxLength);
export const computeShortcutOptions = locale => [
    {
        type: GlobalPickerType.shortcut,
        label: locale.liveTail,
        value: 'live',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last5minutes,
        value: '-5m',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last10minutes,
        value: '-10m',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last30minutes,
        value: '-30m',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last1hour,
        value: '-1h',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last3hours,
        value: '-3h',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last6hours,
        value: '-6h',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last12hours,
        value: '-12h',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last24hours,
        value: '-24h',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last2days,
        value: '-2d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last3days,
        value: '-3d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last7days,
        value: '-7d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last15days,
        value: '-15d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last30days,
        value: '-30d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last90days,
        value: '-90d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last6months,
        value: '-6M',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.last1year,
        value: '-1y',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.today,
        value: '/d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.yesterday,
        value: '-1d/d ~ /d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.thisDayLastWeek,
        value: '-7d/d ~ -6d/d',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.thisWeek,
        value: '/w',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.previousWeek,
        value: '-1w/w ~ /w',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.thisMonth,
        value: '/M',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.previousMonth,
        value: '-1M/M ~ /M',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.thisYear,
        value: '/y',
    },
    {
        type: GlobalPickerType.shortcut,
        label: locale.previousYear,
        value: '-1y/y ~ /y',
    },
];
// 拼接date和time
const computedDateTime = (date, dateFormat, times, timeZone, tzOffset) => {
    const [t0, t1] = times || [];
    if (dateFormat && t0 && t1) {
        const offsetMinutes = getTzOffsetMinutes(tzOffset);
        const validTimeZone = getValidTimeZone(timeZone);
        if (offsetMinutes !== null) {
            const startFormat = t0.split(':').length === 3 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm';
            const endFormat = t1.split(':').length === 3 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm';
            return [
                parseFixedOffsetDate(`${dateFormat} ${t0}`, startFormat, offsetMinutes),
                parseFixedOffsetDate(`${dateFormat} ${t1}`, endFormat, offsetMinutes),
            ];
        }
        if (validTimeZone) {
            return [
                dayjs.tz(`${dateFormat} ${t0}`, validTimeZone),
                dayjs.tz(`${dateFormat} ${t1}`, validTimeZone),
            ];
        }
        return [dayjs(`${dateFormat} ${t0}`), dayjs(`${dateFormat} ${t1}`)];
    }
    if (getTzOffsetMinutes(tzOffset) !== null) {
        const start = startOfInTimeZone(date, 'd', timeZone, tzOffset);
        return [start, endOfDayInTimeZone(date, timeZone, tzOffset)];
    }
    return [date.startOf('d'), date.endOf('d')];
};
// 解析特殊日期
export const parseSpecialTime = (value, specialDate, timeZone, tzOffset) => {
    var _a, _b, _c;
    let dates = null;
    //  不能含有汉字
    if (value && !/[\u4e00-\u9fa5]/.test(value) && ((_a = specialDate === null || specialDate === void 0 ? void 0 : specialDate.dates) === null || _a === void 0 ? void 0 : _a.length)) {
        // 一次特殊日期计算只读取一次当前时间，减少时区投影并保证边界一致。
        const current = getTimeZoneNow(timeZone, tzOffset);
        // 当前
        if (value.includes('%current')) {
            const currentFormat = current === null || current === void 0 ? void 0 : current.format('YYYY-MM-DD');
            if ((_b = specialDate === null || specialDate === void 0 ? void 0 : specialDate.dates) === null || _b === void 0 ? void 0 : _b.includes(currentFormat)) {
                dates = computedDateTime(current, currentFormat, specialDate.times, timeZone, tzOffset);
            }
        }
        else if (value.includes('%last_d')) {
            // 上一
            for (let index = 1; index < specialDate.dates.length; index++) {
                const lastDay = subtractInTimeZone(current, index, 'd', timeZone, tzOffset);
                const lastDayFormat = lastDay === null || lastDay === void 0 ? void 0 : lastDay.format('YYYY-MM-DD');
                if (specialDate.dates.includes(lastDayFormat)) {
                    dates = computedDateTime(lastDay, lastDayFormat, specialDate.times, timeZone, tzOffset);
                    break;
                }
            }
        }
        else if (value.includes('%preious_w_d')) {
            // 上周
            const preiousWeek = subtractInTimeZone(current, 7, 'd', timeZone, tzOffset);
            const preiousWeekFormat = preiousWeek === null || preiousWeek === void 0 ? void 0 : preiousWeek.format('YYYY-MM-DD');
            if ((_c = specialDate === null || specialDate === void 0 ? void 0 : specialDate.dates) === null || _c === void 0 ? void 0 : _c.includes(preiousWeekFormat)) {
                dates = computedDateTime(preiousWeek, preiousWeekFormat, specialDate.times, timeZone, tzOffset);
            }
        }
    }
    return dates;
};
// 通过最大区间过滤快捷选项列表
export const filterShortcutsByMaxDuration = (shortcuts, maxDuration, specialDate, timeZone, tzOffset) => shortcuts === null || shortcuts === void 0 ? void 0 : shortcuts.filter(opt => {
    const { isEqual, isGreater } = durationText(isSpecialTime(opt.value) && (specialDate === null || specialDate === void 0 ? void 0 : specialDate.enable)
        ? parseSpecialTime(opt.value, specialDate || null, timeZone, tzOffset)
        : parseShortcutValue(opt.value, timeZone, tzOffset), maxDuration);
    return !isEqual && !isGreater;
});
// 特殊日期列表
export const computedSpecialList = (locale, specialDate, maxDuration, timeZone, tzOffset) => {
    const hasMax = typeof maxDuration === 'number' && maxDuration > 0 && maxDuration !== MAX_DURATION;
    let list = [
        {
            type: GlobalPickerType.special,
            label: locale.current,
            value: '%current_d%',
        },
        {
            type: GlobalPickerType.special,
            label: locale.previousDay,
            value: '%last_d%',
        },
        {
            type: GlobalPickerType.special,
            label: locale.previousWeekDay,
            value: '%preious_w_d%',
        },
    ];
    if (hasMax) {
        list = list.filter(item => {
            const dates = parseSpecialTime(item.value, specialDate, timeZone, tzOffset);
            if (dates) {
                const { isGreater } = durationText(dates, maxDuration);
                return !isGreater;
            }
            return false;
        });
    }
    return list.map(elem => (Object.assign(Object.assign({}, elem), { label: `${elem.label}${(specialDate === null || specialDate === void 0 ? void 0 : specialDate.name) || ''}` })));
};
// 是否为相对选项
export const isRelatveOpt = (opt) => /\/[Mdwy]/.test(opt.value);
// 是否为特殊日期选项
export const isSpecialOpt = (opt) => isSpecialTime(opt.value);
// 是否为最近选项
export const isRecentOpt = (opt) => !(isRelatveOpt(opt) || isSpecialOpt(opt));
// 是否在特殊日期内
export function isInSpecial(currentDate, specialDate, timeZone, tzOffset) {
    var _a;
    const format = formatInTimeZone(currentDate, 'YYYY-MM-DD', timeZone, tzOffset);
    if (specialDate && specialDate.dates && specialDate.dates.length) {
        return (_a = specialDate === null || specialDate === void 0 ? void 0 : specialDate.dates) === null || _a === void 0 ? void 0 : _a.includes(format);
    }
    return false;
}
// 时区转换为时区偏移值
// 输出格式“UTC+00:00”
export const timezonesToOffset = (tz) => {
    const validTimeZone = getValidTimeZone(tz);
    if (!validTimeZone)
        return '';
    const offset = getTimeZoneNow(validTimeZone).utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset % 60);
    return `UTC${offset >= 0 ? '+' : '-'}${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}`;
};
/** 获取实际生效的 UTC 偏移文本；合法的 tzOffset 优先于 timeZone。 */
export const getUtcOffsetText = (timeZone, tzOffset) => {
    if (tzOffset && getTzOffsetMinutes(tzOffset) !== null)
        return tzOffset;
    return timeZone ? timezonesToOffset(timeZone) : '';
};
